/**
 * The only presentation path for money: `formatCurrency` for a legacy `number`
 * amount, and `format` for an ADR 060 `Money` (ADR 060 — Money Type § 2.7).
 */
import { isSupportedCurrency, minorUnits, type SupportedCurrency } from './currency';
import type { Money } from './money';
import { HALF_UP } from './rounding';

/** Options for {@link formatCurrency}. */
export interface FormatCurrencyOptions {
    /**
     * Fraction digits to show. Default 0 — matches card/bid display today.
     * Use 2 for invoices, settlements, and PDFs.
     */
    decimals?: 0 | 2;
    /** Locale for digit grouping and separator. Default `en-US`. */
    locale?: string;
    /**
     * When true, non-zero amounts are prefixed with `+` or `-`. Useful for
     * settlement deltas. Default false — negatives still get `-`, positives
     * have no sign.
     */
    showSign?: boolean;
    /**
     * When true, append the ISO 4217 code after the formatted amount
     * (e.g. `$1,250 USD`, `CA$1,250 CAD`). Use ONLY on transactional or
     * legal surfaces where a misread currency would cost real money:
     *   - Invoice + settlement PDFs
     *   - Bid confirmation modal
     *   - Clerking hammer / sold confirmation
     *   - Won-lot email + summary screen
     *   - Payment / checkout confirmation
     *
     * Everywhere else (dashboard, cards, tables, bid history, reports,
     * search), leave false. `Intl.NumberFormat` already disambiguates the
     * symbol for $-family currencies (`CA$`, `A$`, `HK$`) — the tenant
     * context makes the currency unambiguous without the extra code.
     *
     * Default false.
     */
    showCurrencyCode?: boolean;
    /**
     * How a negative amount is marked: `'minus'` (the default) prints `-$50`;
     * `'minus-sign'` prints the typographic minus (U+2212) `−$50`, matching a
     * statement whose deducted rows print `−` (BUG-2139); `'parentheses'`
     * prints the accounting form `($50)` — a credit on a balance column
     * (BL-2049). Ignored when `showSign` is set, which already spells the sign out.
     */
    negativeStyle?: NegativeStyle;
}

/** How {@link formatCurrency} marks a negative amount. */
export type NegativeStyle = 'minus' | 'minus-sign' | 'parentheses';

/** The typographic minus sign (U+2212) `'minus-sign'` prints. */
const MINUS_SIGN = '\u2212';

/**
 * Formats a numeric amount in the given ISO 4217 currency.
 *
 * Currency is a required parameter — callers must pass the tenant's (or,
 * later, the auction's) currency alongside the amount. This shape is
 * deliberate: keeping currency on the data rather than a global hook makes
 * the eventual migration to per-auction currency a cheap swap.
 *
 * Falls back to USD when `currency` is nullish or unsupported so a missed
 * callsite during the rollout still renders correctly for USD tenants.
 *
 * @param amount   - Numeric amount in major units (dollars, euros, yen).
 * @param currency - ISO 4217 code. Nullish/unsupported → USD fallback.
 * @param opts     - Decimals, locale, sign display.
 * @returns Localised currency string, e.g. `$1,250`, `€1,250.00`, `-$50`.
 */
/**
 * Cache of `Intl.NumberFormat` instances keyed on the three inputs that affect
 * construction (locale, ISO code, decimal places).
 *
 * Constructing the formatter — not calling `.format()` — is the expensive part,
 * and `formatCurrency` runs once per money cell on invoice / settlement / lot
 * tables, several of which render per row without memoization. Building one
 * formatter per currency-shape instead of per call keeps that off the render
 * path. `sign` and `currencyCode` options are applied to the OUTPUT string, so
 * they are deliberately not part of the key.
 */
const CURRENCY_FORMATTER_CACHE = new Map<string, Intl.NumberFormat>();

/** Build (or reuse) the formatter for a locale + ISO code + decimal count. */
function currencyFormatterFor(
    locale: string,
    iso: SupportedCurrency,
    decimals: number,
): Intl.NumberFormat {
    const key = `${locale}|${iso}|${decimals}`;
    const cached = CURRENCY_FORMATTER_CACHE.get(key);
    if (cached) return cached;
    const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: iso,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
    CURRENCY_FORMATTER_CACHE.set(key, formatter);
    return formatter;
}

export function formatCurrency(
    amount: number,
    currency: string | null | undefined,
    opts: FormatCurrencyOptions = {},
): string {
    const decimals = opts.decimals ?? 0;
    const locale = opts.locale ?? 'en-US';
    const iso: SupportedCurrency = isSupportedCurrency(currency)
        ? currency
        : 'USD';

    const formatted = currencyFormatterFor(locale, iso, decimals).format(Math.abs(amount));

    const negative =
        opts.negativeStyle === 'parentheses'
            ? `(${formatted})`
            : opts.negativeStyle === 'minus-sign'
              ? `${MINUS_SIGN}${formatted}`
              : `-${formatted}`;
    const signed =
        opts.showSign && amount !== 0
            ? amount > 0
                ? `+${formatted}`
                : `-${formatted}`
            : amount < 0
              ? negative
              : formatted;

    return opts.showCurrencyCode ? `${signed} ${iso}` : signed;
}

/**
 * The currency's sign on its own — `$`, `CA$`, `€`, `¥` — for a money input's
 * adornment, where the amount is typed beside it rather than formatted into it.
 * Read off the same `Intl` formatter {@link formatCurrency} uses, so a field's
 * sign and the formatted amounts around it can never name different currencies.
 *
 * Private to {@link moneyInputProps} and typed on a supported currency, so it
 * has no fallback to guess (BUG-1895 retired its exported USD-fallback form).
 */
function currencySign(iso: SupportedCurrency): string {
    const parts = currencyFormatterFor('en-US', iso, 0).formatToParts(0);
    return parts.find((part) => part.type === 'currency')?.value ?? iso;
}

/** What a money input needs from the currency: its sign and its digits. */
export interface MoneyInputProps {
    /** The currency's sign (`$`, `¥`, `CA$`), or undefined while the currency is unknown. */
    prefix: string | undefined;
    /** Whether a decimal point may be typed at all — false for a zero-minor-unit currency. */
    allowDecimals: boolean;
    /** How many digits may follow the point — the currency's minor unit. */
    decimalPlaces: number;
}

/**
 * The prefix and decimal rule a money input takes from its currency — the one
 * source for both apps' money fields (BL-1938, BUG-1874).
 *
 * The minor-unit count is read off the currency (ADR 060 — Money Type), never a
 * literal 2: a yen field is whole-yen, a dollar field takes cents. An unknown or
 * unsupported currency gets no prefix and whole units rather than a guessed "$".
 *
 * @param currency - The ISO 4217 code, or null while the surface's read is in flight.
 * @returns The field's prefix and decimal rule.
 */
export function moneyInputProps(currency: string | null | undefined): MoneyInputProps {
    if (!isSupportedCurrency(currency)) return { prefix: undefined, allowDecimals: false, decimalPlaces: 0 };
    const decimalPlaces = minorUnits(currency);
    return { prefix: currencySign(currency), allowDecimals: decimalPlaces > 0, decimalPlaces };
}

/**
 * Options for {@link format}. The digit count is never a free number — it comes
 * off the currency. The one choice a caller makes is `wholeUnits`: the
 * currency's minor unit (the default) or none (BL-1918, ADR 060 § 2.7).
 */
export type FormatMoneyOptions = Pick<FormatCurrencyOptions, 'locale' | 'showSign' | 'showCurrencyCode' | 'negativeStyle'> & {
    /**
     * `true`: drop the minor unit — `$12,451` rather than `$12,450.50` — for
     * surfaces that show whole amounts (bid ladders, hammer figures). Rounds
     * half-up. `'when-whole'`: drop it only when the amount has none — `$8,000`
     * but `$0.30` — for a figure that is usually whole but must never be
     * rounded (BUG-1508, BUG-1902). A no-op for a currency with no minor unit
     * (JPY) either way.
     */
    wholeUnits?: boolean | 'when-whole';
};

/**
 * Formats a `Money` for display, reading the minor-unit digit count off its
 * currency — so JPY renders as `¥1,000` and USD as `$300.00` without the call
 * site knowing either.
 *
 * @param money - The amount to render.
 * @param opts  - Locale, sign, currency-code display, and whole-unit precision
 *                (`wholeUnits: 'when-whole'` keeps the minor unit of a non-whole amount).
 * @returns The localised display string.
 */
export function format(money: Money, opts: FormatMoneyOptions = {}): string {
    const { wholeUnits = false, ...display } = opts;
    const dropMinor = wholeUnits === 'when-whole' ? money.toDecimal().isInteger() : wholeUnits;
    const decimals = (dropMinor ? 0 : minorUnits(money.currency)) as 0 | 2;
    // Rounded to the display digits in Decimal first (half-up), so the only
    // number that exists is one already exact at the precision Intl will print.
    const shown = money.toDecimal().toDecimalPlaces(decimals, HALF_UP).toNumber();
    return formatCurrency(shown, money.currency, { ...display, decimals });
}

/**
 * Formats a `Money` as a plain decimal for a file a spreadsheet reads (BL-1907) —
 * the currency's minor-unit digits and nothing else: no symbol, no grouping,
 * `-` for a negative. `185000.00` for USD, `1000` for JPY.
 *
 * Rounded half-up in Decimal before printing, so `Number()` never appears
 * (ADR 060 — Money Type § 2.2).
 *
 * @param money - The amount to write.
 * @returns The plain decimal string.
 */
export function formatDecimal(money: Money): string {
    return money.round().toDecimal().toFixed(minorUnits(money.currency));
}
